import React, { useState, useEffect } from 'react';
import api from '../utils/axios';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { toast } from 'sonner';
import { Checkbox } from '../components/ui/checkbox';
import { 
  Download, FileText, FileSpreadsheet, RefreshCw, Calendar,
  BarChart3, Shield, Timer, AlertTriangle, Users, Briefcase,
  CheckCircle, Clock, Loader2, Trash2, Eye, ClipboardList, SlidersHorizontal
} from 'lucide-react';

const CASOS_COLS = [
  ['referencia', 'Referencia'], ['titulo', 'Título'], ['cliente', 'Cliente'], ['cuit', 'CUIT'],
  ['cvu', 'CVU principal'], ['estado', 'Estado'], ['prioridad', 'Prioridad'], ['fecha_creacion', 'Fecha creación'],
  ['fecha_cierre', 'Fecha cierre'], ['asignado', 'Asignado'], ['bloqueo', 'Bloqueo preventivo'],
  ['cierre_def', 'Cierre definitivo'], ['ros', 'ROS'], ['ros_estado', 'ROS estado'], ['ros_uif', 'ROS N.º UIF'],
  ['num_alertas', '# Alertas'], ['resolucion', 'Resolución'],
];
const ALERTAS_COLS = [
  ['id', 'ID'], ['titulo', 'Título'], ['severidad', 'Severidad'], ['estado', 'Estado'],
  ['cuenta', 'Cuenta'], ['monitor', 'Monitor'], ['fecha_creacion', 'Fecha creación'], ['asignado', 'Asignado'],
];

const EXPORT_TYPES = [
  { 
    id: 'compliance', 
    nombre: 'Dashboard Compliance', 
    icono: Shield, 
    descripcion: 'Métricas de cumplimiento, alertas por estado y tendencias',
    color: '#7c3aed'
  },
  { 
    id: 'sla', 
    nombre: 'Dashboard SLAs', 
    icono: Timer, 
    descripcion: 'Tiempos de respuesta, cumplimiento de SLAs por tipo',
    color: '#0891b2'
  },
  { 
    id: 'executive', 
    nombre: 'Dashboard Ejecutivo', 
    icono: BarChart3, 
    descripcion: 'Resumen ejecutivo con KPIs principales',
    color: '#059669'
  },
  { 
    id: 'alerts', 
    nombre: 'Reporte de Alertas', 
    icono: AlertTriangle, 
    descripcion: 'Listado de alertas con filtros y estadísticas',
    color: '#dc2626'
  },
  { 
    id: 'cases', 
    nombre: 'Reporte de Casos', 
    icono: Briefcase, 
    descripcion: 'Casos por estado, prioridad y asignación',
    color: '#2563eb'
  },
  { 
    id: 'users', 
    nombre: 'Reporte de Usuarios', 
    icono: Users, 
    descripcion: 'Actividad de usuarios y métricas de productividad',
    color: '#475569'
  },
];

const ExportCenter = () => {
  const [activeTab, setActiveTab] = useState('nuevo');
  const [loading, setLoading] = useState(false);
  const [exportHistory, setExportHistory] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(true);
  
  // Export form
  const [selectedType, setSelectedType] = useState(null);
  const [exportFormat, setExportFormat] = useState('pdf');
  const [dateRange, setDateRange] = useState({
    desde: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
    hasta: new Date().toISOString().split('T')[0]
  });
  const [isExportDialogOpen, setIsExportDialogOpen] = useState(false);
  const [exportProgress, setExportProgress] = useState(null);
  const [ca, setCa] = useState({ base: 'creacion', medida: 'todas', casos: true, alertas: true, loading: false });
  const [colCasos, setColCasos] = useState(CASOS_COLS.map((c) => c[0]));
  const [colAlertas, setColAlertas] = useState(ALERTAS_COLS.map((c) => c[0]));
  const [showCols, setShowCols] = useState(false);
  const [roiLoading, setRoiLoading] = useState(false);
  const toggleCol = (setter, list, key) => setter(list.includes(key) ? list.filter((k) => k !== key) : [...list, key]);

  useEffect(() => {
    loadExportHistory();
  }, []);

  const loadExportHistory = async () => {
    setLoadingHistory(true);
    try {
      const response = await api.get('/reports/export-history');
      setExportHistory(response.data || []);
    } catch (error) {
      console.error('Error loading export history:', error);
      // Si el endpoint no existe, usar array vacío
      setExportHistory([]);
    } finally {
      setLoadingHistory(false);
    }
  };

  const handleExport = async () => {
    if (!selectedType) {
      toast.error('Selecciona un tipo de reporte');
      return;
    }
    
    setLoading(true);
    setExportProgress({ status: 'generating', message: 'Generando reporte...' });
    
    try {
      const response = await api.post('/reports/export', {
        tipo: selectedType.id,
        formato: exportFormat,
        fecha_desde: dateRange.desde,
        fecha_hasta: dateRange.hasta
      }, {
        responseType: exportFormat === 'pdf' ? 'blob' : 'json',
        timeout: 60000
      });
      
      setExportProgress({ status: 'downloading', message: 'Descargando archivo...' });
      
      // Crear y descargar el archivo
      const blob = new Blob([response.data], { 
        type: exportFormat === 'pdf' ? 'application/pdf' : 
              exportFormat === 'excel' ? 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' :
              'text/csv'
      });
      
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${selectedType.id}_${dateRange.desde}_${dateRange.hasta}.${exportFormat === 'excel' ? 'xlsx' : exportFormat}`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      toast.success('Reporte exportado exitosamente');
      setIsExportDialogOpen(false);
      loadExportHistory();
    } catch (error) {
      console.error('Export error:', error);
      toast.error(error.response?.data?.detail || 'Error al exportar el reporte');
    } finally {
      setLoading(false);
      setExportProgress(null);
    }
  };

  const handleQuickExport = async (type, format) => {
    setLoading(true);
    toast.info(`Generando ${type.nombre} en ${format.toUpperCase()}...`);
    
    try {
      const response = await api.post('/reports/export', {
        tipo: type.id,
        formato: format,
        fecha_desde: dateRange.desde,
        fecha_hasta: dateRange.hasta
      }, {
        responseType: format === 'pdf' ? 'blob' : 'blob',
        timeout: 60000
      });
      
      const blob = new Blob([response.data], { 
        type: format === 'pdf' ? 'application/pdf' : 
              format === 'excel' ? 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' :
              'text/csv'
      });
      
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${type.id}_${new Date().toISOString().split('T')[0]}.${format === 'excel' ? 'xlsx' : format}`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      toast.success('Descarga iniciada');
    } catch (error) {
      toast.error('Error al exportar');
    } finally {
      setLoading(false);
    }
  };

  const openExportDialog = (type) => {
    setSelectedType(type);
    setIsExportDialogOpen(true);
  };

  const handleROI = async (formato) => {
    setRoiLoading(true);
    try {
      const res = await api.post('/reports/roi', {
        formato,
        fecha_desde: dateRange.desde,
        fecha_hasta: dateRange.hasta,
        filtro_medida: ca.medida,
      }, { responseType: 'blob', timeout: 180000 });
      const blob = new Blob([res.data], {
        type: formato === 'pdf' ? 'application/pdf'
          : 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `ROI_operaciones_inusuales_${dateRange.desde}_${dateRange.hasta}.${formato === 'pdf' ? 'pdf' : 'xlsx'}`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      toast.success('ROI generado');
      loadExportHistory();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al generar el ROI');
    } finally {
      setRoiLoading(false);
    }
  };

  const handleInformeCA = async (formato) => {
    if (!ca.casos && !ca.alertas) { toast.error('Elegí al menos Casos o Alertas'); return; }
    if (ca.casos && colCasos.length === 0) { toast.error('Elegí al menos una columna de Casos'); return; }
    if (ca.alertas && colAlertas.length === 0) { toast.error('Elegí al menos una columna de Alertas'); return; }
    setCa((s) => ({ ...s, loading: true }));
    try {
      const res = await api.post('/reports/casos-alertas', {
        formato,
        fecha_desde: dateRange.desde,
        fecha_hasta: dateRange.hasta,
        fecha_base: ca.base,
        filtro_medida: ca.medida,
        incluir_casos: ca.casos,
        incluir_alertas: ca.alertas,
        columnas_casos: ca.casos ? colCasos : undefined,
        columnas_alertas: ca.alertas ? colAlertas : undefined,
      }, { responseType: 'blob', timeout: 120000 });
      const blob = new Blob([res.data], {
        type: formato === 'pdf' ? 'application/pdf'
          : 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `informe_casos_alertas_${dateRange.desde}_${dateRange.hasta}.${formato === 'pdf' ? 'pdf' : 'xlsx'}`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      toast.success('Informe generado');
      loadExportHistory();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al generar el informe');
    } finally {
      setCa((s) => ({ ...s, loading: false }));
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('es-AR', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusBadge = (status) => {
    const styles = {
      'completed': { bg: '#dcfce7', color: '#166534', label: 'Completado' },
      'pending': { bg: '#fef3c7', color: '#b45309', label: 'Pendiente' },
      'failed': { bg: '#fee2e2', color: '#dc2626', label: 'Error' },
      'processing': { bg: '#dbeafe', color: '#1e40af', label: 'Procesando' },
    };
    const style = styles[status] || styles.pending;
    return (
      <Badge style={{ backgroundColor: style.bg, color: style.color }}>
        {style.label}
      </Badge>
    );
  };

  return (
    <div className="p-6 lg:p-8 h-full overflow-auto bg-slate-50">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
            <Download size={28} className="text-emerald-600" />
            Centro de Exportación
          </h1>
          <p className="text-slate-600 mt-1">Genera y descarga reportes en PDF, Excel o CSV</p>
        </div>
        <Button onClick={loadExportHistory} variant="outline" size="sm">
          <RefreshCw size={16} className="mr-2" />
          Actualizar
        </Button>
      </div>

      {/* Informe de Alertas y Casos (principal) */}
      <Card className="p-5 lg:p-6 mb-6 bg-white border-l-4 border-l-indigo-500" data-testid="ca-informe-card">
        <div className="flex items-start gap-3 mb-4">
          <div className="p-2 rounded-lg bg-indigo-100"><ClipboardList size={24} className="text-indigo-600" /></div>
          <div>
            <h2 className="text-lg font-semibold text-slate-900">Informe de Alertas y Casos</h2>
            <p className="text-sm text-slate-500">Casos con estado y medidas (bloqueo preventivo, ROS, cierre definitivo) más el listado de alertas, por rango de fechas. Exportá a Excel (tabla) o PDF.</p>
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <div>
            <Label className="text-xs text-slate-600">Fecha desde</Label>
            <Input type="date" value={dateRange.desde} onChange={(e) => setDateRange({ ...dateRange, desde: e.target.value })} className="mt-1.5" data-testid="ca-informe-desde" />
          </div>
          <div>
            <Label className="text-xs text-slate-600">Fecha hasta</Label>
            <Input type="date" value={dateRange.hasta} onChange={(e) => setDateRange({ ...dateRange, hasta: e.target.value })} className="mt-1.5" data-testid="ca-informe-hasta" />
          </div>
          <div>
            <Label className="text-xs text-slate-600">El rango filtra por</Label>
            <Select value={ca.base} onValueChange={(v) => setCa({ ...ca, base: v })}>
              <SelectTrigger className="mt-1.5" data-testid="ca-informe-base"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="creacion">Fecha de creación</SelectItem>
                <SelectItem value="medida">Fecha de la medida</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs text-slate-600">Medida</Label>
            <Select value={ca.medida} onValueChange={(v) => setCa({ ...ca, medida: v })}>
              <SelectTrigger className="mt-1.5" data-testid="ca-informe-medida"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="todas">Todas las medidas</SelectItem>
                <SelectItem value="bloqueo">Solo con bloqueo preventivo</SelectItem>
                <SelectItem value="ros">Solo con ROS</SelectItem>
                <SelectItem value="cierre">Solo con cierre definitivo</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-6 mt-4">
          <div className="flex items-center gap-2">
            <Checkbox id="ca-inc-casos" checked={ca.casos} onCheckedChange={(v) => setCa({ ...ca, casos: !!v })} data-testid="ca-informe-incluir-casos" />
            <Label htmlFor="ca-inc-casos" className="text-sm cursor-pointer">Incluir Casos</Label>
          </div>
          <div className="flex items-center gap-2">
            <Checkbox id="ca-inc-alertas" checked={ca.alertas} onCheckedChange={(v) => setCa({ ...ca, alertas: !!v })} data-testid="ca-informe-incluir-alertas" />
            <Label htmlFor="ca-inc-alertas" className="text-sm cursor-pointer">Incluir Alertas</Label>
          </div>
          <Button variant="ghost" size="sm" onClick={() => setShowCols((s) => !s)} className="text-slate-600" data-testid="ca-informe-columnas-toggle">
            <SlidersHorizontal size={16} className="mr-1.5" /> {showCols ? 'Ocultar columnas' : 'Personalizar columnas'}
          </Button>
          <div className="flex gap-2 ml-auto">
            <Button onClick={() => handleInformeCA('excel')} disabled={ca.loading} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="ca-informe-excel-btn">
              {ca.loading ? <Loader2 className="animate-spin mr-2" size={16} /> : <FileSpreadsheet size={16} className="mr-2" />} Excel
            </Button>
            <Button onClick={() => handleInformeCA('pdf')} disabled={ca.loading} variant="outline" data-testid="ca-informe-pdf-btn">
              {ca.loading ? <Loader2 className="animate-spin mr-2" size={16} /> : <FileText size={16} className="mr-2" />} PDF
            </Button>
          </div>
        </div>
        {showCols && (
          <div className="mt-4 pt-4 border-t border-slate-200 grid grid-cols-1 lg:grid-cols-2 gap-6" data-testid="ca-informe-columnas">
            {ca.casos && (
              <div>
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-semibold text-slate-700">Columnas de Casos</span>
                  <div className="flex gap-3">
                    <button type="button" className="text-xs text-indigo-600 hover:underline" onClick={() => setColCasos(CASOS_COLS.map((c) => c[0]))} data-testid="ca-col-casos-todas">Todas</button>
                    <button type="button" className="text-xs text-slate-400 hover:underline" onClick={() => setColCasos([])} data-testid="ca-col-casos-ninguna">Ninguna</button>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-x-4 gap-y-1.5">
                  {CASOS_COLS.map(([k, label]) => (
                    <label key={k} className="flex items-center gap-2 text-sm cursor-pointer" data-testid={`ca-col-caso-${k}`}>
                      <Checkbox checked={colCasos.includes(k)} onCheckedChange={() => toggleCol(setColCasos, colCasos, k)} />
                      <span className="text-slate-600">{label}</span>
                    </label>
                  ))}
                </div>
              </div>
            )}
            {ca.alertas && (
              <div>
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-semibold text-slate-700">Columnas de Alertas</span>
                  <div className="flex gap-3">
                    <button type="button" className="text-xs text-indigo-600 hover:underline" onClick={() => setColAlertas(ALERTAS_COLS.map((c) => c[0]))} data-testid="ca-col-alertas-todas">Todas</button>
                    <button type="button" className="text-xs text-slate-400 hover:underline" onClick={() => setColAlertas([])} data-testid="ca-col-alertas-ninguna">Ninguna</button>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-x-4 gap-y-1.5">
                  {ALERTAS_COLS.map(([k, label]) => (
                    <label key={k} className="flex items-center gap-2 text-sm cursor-pointer" data-testid={`ca-col-alerta-${k}`}>
                      <Checkbox checked={colAlertas.includes(k)} onCheckedChange={() => toggleCol(setColAlertas, colAlertas, k)} />
                      <span className="text-slate-600">{label}</span>
                    </label>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </Card>

      {/* ROI - Registro de Operaciones Inusuales (formato regulatorio) */}
      <Card className="p-5 lg:p-6 mb-6 bg-white border-l-4 border-l-amber-500" data-testid="roi-card">
        <div className="flex flex-col md:flex-row md:items-center gap-4">
          <div className="flex items-start gap-3 flex-1">
            <div className="p-2 rounded-lg bg-amber-100"><AlertTriangle size={24} className="text-amber-600" /></div>
            <div>
              <h2 className="text-lg font-semibold text-slate-900">ROI — Registro de Operaciones Inusuales</h2>
              <p className="text-sm text-slate-500">Formato regulatorio (una fila por operación inusual). Usa el <span className="font-medium">rango de fechas</span> y el <span className="font-medium">filtro de medida</span> de arriba. El rango filtra por la fecha de la operación.</p>
            </div>
          </div>
          <div className="flex gap-2 md:ml-auto">
            <Button onClick={() => handleROI('excel')} disabled={roiLoading} className="bg-amber-600 hover:bg-amber-700 text-white" data-testid="roi-excel-btn">
              {roiLoading ? <Loader2 className="animate-spin mr-2" size={16} /> : <FileSpreadsheet size={16} className="mr-2" />} ROI Excel
            </Button>
            <Button onClick={() => handleROI('pdf')} disabled={roiLoading} variant="outline" data-testid="roi-pdf-btn">
              {roiLoading ? <Loader2 className="animate-spin mr-2" size={16} /> : <FileText size={16} className="mr-2" />} ROI PDF
            </Button>
          </div>
        </div>
      </Card>

      {/* Quick Export Cards */}
      <div className="mb-6">
        <h2 className="text-lg font-semibold text-slate-900 mb-4">Otros reportes rápidos</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {EXPORT_TYPES.map(type => {
            const Icon = type.icono;
            return (
              <Card key={type.id} className="p-4 bg-white hover:shadow-md transition-shadow">
                <div className="flex items-start gap-3">
                  <div 
                    className="p-2 rounded-lg"
                    style={{ backgroundColor: `${type.color}20` }}
                  >
                    <Icon size={24} style={{ color: type.color }} />
                  </div>
                  <div className="flex-1">
                    <h3 className="font-semibold text-slate-900">{type.nombre}</h3>
                    <p className="text-sm text-slate-500 mt-1">{type.descripcion}</p>
                    <div className="flex gap-2 mt-3">
                      <Button 
                        size="sm" 
                        variant="outline"
                        onClick={() => handleQuickExport(type, 'pdf')}
                        disabled={loading}
                        className="text-xs"
                      >
                        <FileText size={14} className="mr-1" />
                        PDF
                      </Button>
                      <Button 
                        size="sm" 
                        variant="outline"
                        onClick={() => handleQuickExport(type, 'excel')}
                        disabled={loading}
                        className="text-xs"
                      >
                        <FileSpreadsheet size={14} className="mr-1" />
                        Excel
                      </Button>
                      <Button 
                        size="sm" 
                        variant="ghost"
                        onClick={() => openExportDialog(type)}
                        className="text-xs"
                      >
                        <Calendar size={14} className="mr-1" />
                        Personalizar
                      </Button>
                    </div>
                  </div>
                </div>
              </Card>
            );
          })}
        </div>
      </div>

      {/* Date Range Filter */}
      <Card className="p-4 mb-6 bg-white">
        <div className="flex flex-wrap items-end gap-4">
          <div>
            <Label className="text-sm text-slate-600">Rango de fechas para exportaciones</Label>
            <div className="flex gap-2 mt-2">
              <Input
                type="date"
                value={dateRange.desde}
                onChange={(e) => setDateRange({ ...dateRange, desde: e.target.value })}
                className="w-40"
              />
              <span className="flex items-center text-slate-400">a</span>
              <Input
                type="date"
                value={dateRange.hasta}
                onChange={(e) => setDateRange({ ...dateRange, hasta: e.target.value })}
                className="w-40"
              />
            </div>
          </div>
          <div className="flex gap-2">
            <Button 
              variant="outline" 
              size="sm"
              onClick={() => setDateRange({
                desde: new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
                hasta: new Date().toISOString().split('T')[0]
              })}
            >
              Última semana
            </Button>
            <Button 
              variant="outline" 
              size="sm"
              onClick={() => setDateRange({
                desde: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
                hasta: new Date().toISOString().split('T')[0]
              })}
            >
              Último mes
            </Button>
            <Button 
              variant="outline" 
              size="sm"
              onClick={() => setDateRange({
                desde: new Date(Date.now() - 90 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
                hasta: new Date().toISOString().split('T')[0]
              })}
            >
              Último trimestre
            </Button>
          </div>
        </div>
      </Card>

      {/* Export History */}
      <Card className="bg-white">
        <div className="p-4 border-b border-slate-200">
          <h2 className="font-semibold text-slate-900">Historial de Exportaciones</h2>
        </div>
        {loadingHistory ? (
          <div className="p-8 flex items-center justify-center">
            <RefreshCw className="animate-spin text-slate-400" size={24} />
          </div>
        ) : exportHistory.length === 0 ? (
          <div className="p-8 text-center text-slate-500">
            <Download size={48} className="mx-auto mb-4 text-slate-300" />
            <p>No hay exportaciones recientes</p>
            <p className="text-sm mt-1">Las exportaciones que realices aparecerán aquí</p>
          </div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Reporte</TableHead>
                <TableHead>Formato</TableHead>
                <TableHead>Período</TableHead>
                <TableHead>Fecha</TableHead>
                <TableHead>Estado</TableHead>
                <TableHead className="text-right">Acciones</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {exportHistory.map((exp, idx) => (
                <TableRow key={exp.id || idx}>
                  <TableCell className="font-medium">{exp.tipo_nombre || exp.tipo}</TableCell>
                  <TableCell>
                    <Badge variant="outline">{exp.formato?.toUpperCase()}</Badge>
                  </TableCell>
                  <TableCell className="text-sm text-slate-500">
                    {exp.fecha_desde} - {exp.fecha_hasta}
                  </TableCell>
                  <TableCell className="text-sm text-slate-500">
                    {formatDate(exp.fecha_creacion)}
                  </TableCell>
                  <TableCell>{getStatusBadge(exp.estado)}</TableCell>
                  <TableCell className="text-right">
                    {exp.estado === 'completed' && exp.url && (
                      <Button size="sm" variant="ghost" asChild>
                        <a href={exp.url} download>
                          <Download size={14} />
                        </a>
                      </Button>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </Card>

      {/* Export Dialog */}
      <Dialog open={isExportDialogOpen} onOpenChange={setIsExportDialogOpen}>
        <DialogContent style={{ backgroundColor: '#ffffff' }}>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {selectedType && (
                <>
                  <selectedType.icono size={20} style={{ color: selectedType.color }} />
                  Exportar {selectedType.nombre}
                </>
              )}
            </DialogTitle>
            <DialogDescription>
              Configura las opciones de exportación
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div>
              <Label>Formato de exportación</Label>
              <Select value={exportFormat} onValueChange={setExportFormat}>
                <SelectTrigger className="mt-2">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="pdf">
                    <div className="flex items-center gap-2">
                      <FileText size={16} className="text-red-600" />
                      PDF - Documento portable
                    </div>
                  </SelectItem>
                  <SelectItem value="excel">
                    <div className="flex items-center gap-2">
                      <FileSpreadsheet size={16} className="text-green-600" />
                      Excel - Hoja de cálculo
                    </div>
                  </SelectItem>
                  <SelectItem value="csv">
                    <div className="flex items-center gap-2">
                      <FileText size={16} className="text-blue-600" />
                      CSV - Valores separados por coma
                    </div>
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Fecha desde</Label>
                <Input
                  type="date"
                  value={dateRange.desde}
                  onChange={(e) => setDateRange({ ...dateRange, desde: e.target.value })}
                  className="mt-2"
                />
              </div>
              <div>
                <Label>Fecha hasta</Label>
                <Input
                  type="date"
                  value={dateRange.hasta}
                  onChange={(e) => setDateRange({ ...dateRange, hasta: e.target.value })}
                  className="mt-2"
                />
              </div>
            </div>
            
            {exportProgress && (
              <div className="p-4 bg-blue-50 rounded-lg flex items-center gap-3">
                <Loader2 className="animate-spin text-blue-600" size={20} />
                <span className="text-blue-700">{exportProgress.message}</span>
              </div>
            )}
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsExportDialogOpen(false)}>
              Cancelar
            </Button>
            <Button 
              onClick={handleExport} 
              disabled={loading}
              style={{ backgroundColor: selectedType?.color || '#059669', color: 'white' }}
            >
              {loading ? (
                <>
                  <Loader2 className="animate-spin mr-2" size={16} />
                  Exportando...
                </>
              ) : (
                <>
                  <Download size={16} className="mr-2" />
                  Exportar
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ExportCenter;
